<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue';
import type { AuthSubmitMode } from '@/entities/session/auth-errors';
import { sendLoginSms } from '@/features/auth/auth-api';
import { lorUsAuthCopy as copy } from '@/features/auth/lor-us-auth-copy';
import { clientEnv } from '@/shared/config/env';
import { useSessionStore } from '@/stores/session-store';

const LOR_ASSETS = {
  banner: '/skins/lor-us-h5/auth/login-banner.png',
  phone: '/skins/lor-us-h5/auth/phone-icon.png',
} as const;

const props = withDefaults(
  defineProps<{
    mode?: AuthSubmitMode;
    referralCode?: string;
  }>(),
  { mode: 'login', referralCode: '' },
);

const emit = defineEmits<{
  close: [];
  success: [];
}>();

void props.mode;

const sessionStore = useSessionStore();
const phoneNumber = ref('');
const smsCode = ref('');
const loading = ref(false);
const sendingSms = ref(false);
const smsCountdown = ref(0);
const localError = ref('');
let smsTimer: ReturnType<typeof setInterval> | null = null;

const rewardText = computed(() => `$${copy.defaultReward}`);
const smsBtnLabel = computed(() =>
  smsCountdown.value > 0 ? `${smsCountdown.value}s` : copy.smsName,
);
const okLabel = computed(() => (loading.value ? copy.loading : copy.confirm));
const guestLabel = computed(() => (loading.value ? copy.loading : copy.guest));
const visibleError = computed(() => localError.value || sessionStore.lastError);

function clearSmsTimer() {
  if (smsTimer) {
    clearInterval(smsTimer);
    smsTimer = null;
  }
}

onBeforeUnmount(clearSmsTimer);

/** 源站 validateUSPhoneNumber */
function validateUSPhoneNumber(value: string) {
  if (/[^0-9]/.test(value)) return false;
  if (value.length !== 10) return false;
  if (value[0] === '0' || value[0] === '1') return false;
  if (value[3] === '0' || value[3] === '1') return false;
  return true;
}

function onPhoneInput(event: Event) {
  const target = event.target as HTMLInputElement;
  phoneNumber.value = target.value.replace(/\D/g, '').slice(0, 10);
}

function onSmsInput(event: Event) {
  const target = event.target as HTMLInputElement;
  smsCode.value = target.value.replace(/\D/g, '').slice(0, 6);
}

function startSmsCountdown() {
  smsCountdown.value = 60;
  clearSmsTimer();
  smsTimer = setInterval(() => {
    smsCountdown.value -= 1;
    if (smsCountdown.value <= 0) clearSmsTimer();
  }, 1000);
}

async function sendSmsCode() {
  localError.value = '';
  if (!phoneNumber.value) {
    localError.value = copy.telephone;
    return;
  }
  if (!validateUSPhoneNumber(phoneNumber.value)) {
    localError.value = copy.phoneError;
    return;
  }
  sendingSms.value = true;
  try {
    await sendLoginSms({
      phone: `${copy.phonePre}${phoneNumber.value}`,
      siteId: clientEnv.siteId,
    });
    startSmsCountdown();
  } catch {
    localError.value = copy.smsFail;
  } finally {
    sendingSms.value = false;
  }
}

async function loginByPhone() {
  localError.value = '';
  sessionStore.clearError();
  if (!phoneNumber.value) {
    localError.value = copy.telephone;
    return;
  }
  if (!validateUSPhoneNumber(phoneNumber.value)) {
    localError.value = copy.phoneError;
    return;
  }
  if (!smsCode.value) {
    localError.value = copy.enterSms;
    return;
  }

  loading.value = true;
  try {
    const ok = await sessionStore.loginBySms({
      phone: `${copy.phonePre}${phoneNumber.value}`,
      code: smsCode.value,
      referralCode: props.referralCode.trim() || undefined,
    });
    if (ok) emit('success');
  } finally {
    loading.value = false;
  }
}

async function loginByGuest() {
  localError.value = '';
  sessionStore.clearError();
  loading.value = true;
  try {
    const ok = await sessionStore.loginAsGuest();
    if (ok) emit('success');
  } finally {
    loading.value = false;
  }
}
</script>

<template>
  <div class="loginType-all">
    <div class="loginTypeBg">
      <header class="public-customizeHead-v8">
        <button class="americanPublic-back" type="button" aria-label="Back" @click="emit('close')">
          <span class="americanPublic-back-i" aria-hidden="true" />
        </button>
        <div class="public-customizeHead-title-v8">
          <div class="public-customizeHead-titleText-v8">{{ copy.pageTitle }}</div>
        </div>
      </header>
      <div class="public-customizeHead-placeholder-v8" />

      <div class="loginType-main">
        <div class="loginType-banner">
          <img class="loginType-bannerImg" :src="LOR_ASSETS.banner" alt="" />
          <div class="loginType-bannerNum">{{ rewardText }}</div>
        </div>

        <div class="loginType-enterAll">
          <div class="loginType-enter">
            <div class="loginType-enter-phone">
              <div class="loginType-enter-area">{{ copy.phonePre }}</div>
              <input
                class="loginType-enter-phoneNum"
                type="tel"
                inputmode="numeric"
                maxlength="10"
                :value="phoneNumber"
                :placeholder="copy.telephone"
                @input="onPhoneInput"
              />
              <img class="loginType-enter-phoneIcon" :src="LOR_ASSETS.phone" alt="" />
            </div>

            <div class="loginType-enter-verify">
              <input
                class="loginType-enter-sms"
                type="tel"
                inputmode="numeric"
                maxlength="6"
                :value="smsCode"
                :placeholder="copy.enterSms"
                @input="onSmsInput"
              />
              <button
                class="loginType-enter-smsBtn"
                type="button"
                :disabled="sendingSms || smsCountdown > 0"
                @click="sendSmsCode"
              >
                {{ smsBtnLabel }}
              </button>
            </div>
          </div>

          <p v-if="visibleError" class="loginType-error">{{ visibleError }}</p>

          <div class="loginType-btn">
            <button
              class="loginType-btn-button loginType-button-scale-v8"
              type="button"
              :disabled="loading"
              @click="loginByPhone"
            >
              {{ okLabel }}
            </button>
          </div>

          <div class="loginType-dividingLine">
            <div class="loginType-line" />
            <div class="loginType-line-text">{{ copy.or }}</div>
            <div class="loginType-line" />
          </div>

          <div class="loginType-guest">
            <button
              class="loginType-guest-button loginType-button-scale-v8"
              type="button"
              :disabled="loading"
              @click="loginByGuest"
            >
              {{ guestLabel }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.loginType-button-scale-v8:active {
  position: relative;
  transform: scale(0.98);
  opacity: 0.9;
}

.loginType-all {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  flex-direction: column;
  font-family: Calibri, sans-serif;
}

.loginTypeBg {
  width: 100%;
  min-height: 100%;
  display: flex;
  align-items: center;
  flex-direction: column;
  background: #181515;
}

.public-customizeHead-v8 {
  width: 100%;
  height: 3.4375rem;
  display: flex;
  background: #1b1c26;
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 3;
  border-bottom: 1px solid #1b1c26;
}

.public-customizeHead-placeholder-v8 {
  width: 100%;
  height: 3.4375rem;
  display: flex;
  flex-shrink: 0;
}

.americanPublic-back {
  width: 2.4375rem;
  height: 3.4375rem;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  border: 0;
  padding: 0;
  background: transparent;
  cursor: pointer;
}

.americanPublic-back-i {
  width: 0.625rem;
  height: 0.625rem;
  margin-left: 0.875rem;
  border-left: 2px solid #fff;
  border-bottom: 2px solid #fff;
  transform: rotate(45deg);
}

.public-customizeHead-title-v8 {
  width: calc(100% - 4.875rem);
  height: 3.4375rem;
  display: flex;
  margin-right: 2.4375rem;
  align-items: center;
  overflow: hidden;
}

.public-customizeHead-titleText-v8 {
  width: 100%;
  height: auto;
  line-height: 1.125rem;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.125rem;
}

.loginType-main {
  width: 100%;
  height: auto;
  display: flex;
  align-items: center;
  flex-direction: column;
  margin-bottom: 0.5rem;
}

.loginType-banner {
  width: 100%;
  height: auto;
  display: flex;
  align-items: center;
  flex-direction: column;
  margin-bottom: 0.75rem;
  position: relative;
}

.loginType-bannerImg {
  width: 100%;
  height: auto;
  display: block;
}

.loginType-bannerNum {
  position: absolute;
  top: 40%;
  left: 32%;
  font-size: 1.875rem;
  color: #ffcb35;
  text-shadow: 0 2px 0 #c32900;
  z-index: 1;
  line-height: 1;
  font-weight: 400;
}

.loginType-enterAll {
  box-sizing: border-box;
  width: 100%;
  height: auto;
  display: flex;
  align-items: center;
  flex-direction: column;
  padding: 0 0.75rem;
  margin-bottom: 0.5rem;
}

.loginType-enter {
  width: 100%;
  height: auto;
  display: flex;
  align-items: center;
  flex-direction: column;
}

.loginType-enter-phone {
  box-sizing: border-box;
  width: 100%;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #000;
  border-radius: 3.125rem;
  padding: 0.5rem 1.125rem;
  margin-bottom: 0.75rem;
}

.loginType-enter-area {
  width: auto;
  font-size: 0.9375rem;
  color: #fff;
  line-height: 1.4375rem;
  height: 1.4375rem;
  flex-shrink: 0;
}

.loginType-enter-phoneNum {
  width: auto;
  font-size: 0.9375rem;
  color: #fff;
  line-height: 1.4375rem;
  height: 1.4375rem;
  margin: 0 0.5rem;
  flex: 1;
  border: 0;
  outline: none;
  background: transparent;
  font-family: Calibri, sans-serif;
  min-width: 0;
}

.loginType-enter-phoneNum::placeholder {
  color: #818e99;
}

.loginType-enter-phoneIcon {
  height: 1.4375rem;
  width: auto;
  display: block;
  flex-shrink: 0;
}

.loginType-enter-verify {
  width: 100%;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.loginType-enter-sms {
  box-sizing: border-box;
  width: auto;
  display: flex;
  align-items: center;
  background: #000;
  border-radius: 3.125rem;
  padding: 0.5rem 0.75rem;
  margin-right: 0.5rem;
  flex: 1;
  font-size: 0.9375rem;
  line-height: 1.4375rem;
  height: auto;
  min-height: calc(1.4375rem + 1rem);
  color: #fff;
  border: 0;
  outline: none;
  font-family: Calibri, sans-serif;
}

.loginType-enter-sms::placeholder {
  color: #818e99;
}

.loginType-enter-smsBtn {
  box-sizing: border-box;
  width: 5rem;
  height: 2.4375rem;
  padding: 0 0.5rem;
  background: linear-gradient(to bottom, #3fc7cd, #227c82);
  border-radius: 3.125rem;
  border: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  line-height: 2.4375rem;
  color: #f0ffea;
  font-family: Calibri, sans-serif;
  cursor: pointer;
  flex-shrink: 0;
}

.loginType-enter-smsBtn:disabled {
  color: #fff;
  background: linear-gradient(to bottom, #b0b0b0, #949494);
  cursor: not-allowed;
}

.loginType-error {
  width: 100%;
  margin: 0 0 0.25rem;
  color: #ff6b6b;
  font-size: 0.75rem;
  line-height: 1.125rem;
  text-align: left;
}

.loginType-btn {
  width: 100%;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  margin: 0.75rem 0;
}

.loginType-btn-button {
  width: 100%;
  height: 2.875rem;
  line-height: 2.875rem;
  background: linear-gradient(to bottom, #3fc7cd, #227c82);
  border-radius: 3.125rem;
  border: 0;
  font-size: 1rem;
  color: #f0ffea;
  padding: 0 1.25rem;
  font-weight: 700;
  font-family: Calibri, sans-serif;
  cursor: pointer;
}

.loginType-btn-button:disabled {
  color: #fff;
  background: linear-gradient(to bottom, #b0b0b0, #949494);
  cursor: not-allowed;
}

.loginType-dividingLine {
  width: 100%;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.loginType-line {
  flex: 1 1 auto;
  height: 1px;
  background: #43464b;
}

.loginType-line-text {
  padding: 0 0.75rem;
  font-size: 0.875rem;
  color: #77828c;
  line-height: 1rem;
}

.loginType-guest {
  width: 100%;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  margin: 0.75rem 0;
}

.loginType-guest-button {
  box-sizing: border-box;
  width: calc(100% - 2px);
  height: 2.875rem;
  line-height: 2.875rem;
  background: linear-gradient(to bottom, #363f46, #343d44);
  border-radius: 3.125rem;
  border: 1px solid #8d9296;
  font-size: 1rem;
  color: #f0ffea;
  padding: 0 1.25rem;
  font-weight: 700;
  font-family: Calibri, sans-serif;
  cursor: pointer;
}

.loginType-guest-button:disabled {
  color: #f0ffea;
  background: linear-gradient(to bottom, #363f46, #343d44);
  border: 1px solid #8d9296;
  cursor: not-allowed;
}
</style>
